import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, useTheme, styled, Button, Menu, MenuItem, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import BackButton from "components/common/SiteTemplate/BackButton/BackButton";
import Footer from "components/common/SiteTemplate/Footer/Footer";
import HelpPanel from "components/common/SiteTemplate/HelpPanel/HelpPanel";
import Nav from "components/common/SiteTemplate/Nav/Nav";
import PageMenu from "components/common/SiteTemplate/PageMenu/PageMenu";
import Sidebar from "components/common/SiteTemplate/Sidebar/Sidebar";
import UserContext from "Context/UserProvider";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthService } from "service/AuthService/AuthService";
import generatePageInfo from "./generatePageInfo";

const drawerWidth = 240;

const LayoutRoot = styled('div')({
  minHeight: '100vh',
  width: '100%',
  backgroundColor: 'background.default',
  display: 'flex'
});

const SideNav = styled('aside')(({ theme }) => ({
  width: drawerWidth,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  marginTop: 64,
  position: 'fixed',
  top: 0,
  bottom: 0,
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

interface MainContentProps {
  helpPanelOpen: boolean;
}

const MainContent = styled('main')<MainContentProps>(({ theme, helpPanelOpen }) => ({
  flex: 1,
  marginTop: 64,
  [theme.breakpoints.up('md')]: {
    marginLeft: drawerWidth
  },
  overflowY: 'auto',
  height: 'calc(100vh - 64px)',
  '& > section': {
    width: helpPanelOpen ? `calc(100% - 385px)` : '100%',
    padding: 0,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2)
    }
  }
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(1.5, 3)
  }
}));

const HelpSidebar = styled('aside')(({ theme }) => ({
  width: 385,
  position: 'fixed',
  right: 0,
  top: 64,
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.divider}`,
  overflow: 'auto',
  zIndex: 1
}));

function ProtectedRouteLayout(props: any) {
  const [helpPanel, setHelpPanel] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const {userInfo, setUserInfo, selectedProduct} = useContext(UserContext);
  const { logout } = useAuthService();
  const location = useLocation();
  const pageInfo = generatePageInfo(location.pathname, location.state, userInfo.userType, selectedProduct, userInfo.businessCategory) as any;
  const [activeMenu, setActiveMenu] = useState(pageInfo.active_menu);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleCtaClick = (url: string, action?: boolean, state?: any) => {
    handleMenuClose();
    if (action) {
      // Handle action if needed
    } else {
      navigate(url, { state });
    }
  };

  const handleProfileClick = () => {
    setDrawerOpen(false);
    navigate('/common/profile');
  };

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
  };

  return (
    <LayoutRoot>
      <AppBar 
        position="fixed"
        sx={{ 
          width: '100%',
          bgcolor: '#2A3137',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          <Nav
            sidebar={drawerOpen}
            controlSideBar={() => setDrawerOpen(!drawerOpen)}
            setSidebar={setDrawerOpen}
            businessName={props.businessName}
            notifications={props.notifications}
            userInfo={[userInfo, setUserInfo]}
            hideMobileMenu={true}
          />
        </Toolbar>
      </AppBar>

      {/* Desktop Sidebar */}
      <SideNav>
        <PageMenu
          menu={pageInfo.menu}
          activeMenu={[activeMenu, setActiveMenu]}
          privateChargingNetworkSubscription={userInfo.privateChargingNetworkSubscription}
        />
      </SideNav>

      <MainContent helpPanelOpen={helpPanel}>
        <TitleBar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            {pageInfo.back && <BackButton backUrl={pageInfo.backUrl} />}
            <Typography variant="h5" color="text.primary" sx={{ fontWeight: 500 }}>
              {pageInfo.second_title || pageInfo.active_menu}
            </Typography>
            {/* Desktop CTA buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 2 }}>
              {pageInfo.ctaButton && pageInfo.ctaButton.map((button: any, index: number) => (
                <Button
                  key={index}
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleCtaClick(button.url, button.action, button.state)}
                >
                  {button.title}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Desktop help icon */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <IconButton onClick={() => setHelpPanel(!helpPanel)}>
              <HelpOutlineIcon />
            </IconButton>
          </Box>

          {/* Mobile view */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            alignItems: 'center',
            gap: 1
          }}>
            {pageInfo.ctaButton && pageInfo.ctaButton.length > 0 && (
              <IconButton onClick={handleMenuClick}>
                <MoreVertIcon />
              </IconButton>
            )}
            <IconButton onClick={() => setHelpPanel(!helpPanel)}>
              <HelpOutlineIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {pageInfo.ctaButton && pageInfo.ctaButton.map((button: any, index: number) => (
                <MenuItem 
                  key={index}
                  onClick={() => handleCtaClick(button.url, button.action, button.state)}
                >
                  <Typography>{button.title}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </TitleBar>

        <section>
          {props.children}
        </section>

        {helpPanel && (
          <HelpSidebar>
            <HelpPanel
              title={pageInfo.title}
              content={pageInfo.helpText ?? "help menu"}
              onClose={() => setHelpPanel(false)}
            />
          </HelpSidebar>
        )}
      </MainContent>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: '100%',
            marginTop: '64px',
            height: 'calc(100% - 64px)'
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          bgcolor: 'background.paper'
        }}>
          {/* Main menu content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <PageMenu
              menu={pageInfo.menu}
              activeMenu={[activeMenu, setActiveMenu]}
              privateChargingNetworkSubscription={userInfo.privateChargingNetworkSubscription}
              onMenuItemClick={() => setDrawerOpen(false)}
            />
          </Box>
          
          {/* Profile and Logout links at bottom */}
          <Box sx={{ 
            p: 0
          }}>
            <MenuItem onClick={handleProfileClick} sx={{ py: 1 }}>
              <AccountCircleIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography>My Profile</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
              <LogoutIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Typography>Sign out</Typography>
            </MenuItem>
          </Box>
        </Box>
      </Drawer>
    </LayoutRoot>
  );
}

export default ProtectedRouteLayout;
